import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { useState, type ReactNode } from 'react';

import { env } from '@/env';

/**
 * Wraps the app in TanStack Query. Single QueryClient per app instance.
 *
 * Defaults:
 *   - staleTime: 30s. Reads stay fresh long enough to avoid duplicate fetches
 *     within a screen, but refetch reliably between screens.
 *   - retry: 1. Fail fast on dev; production errors are visible via Sentry.
 *   - refetchOnWindowFocus: false. CRM users tab in/out constantly; refetch
 *     would burn API quota for little user benefit.
 */
export const AppQueryProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            gcTime: 5 * 60 * 1000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
          mutations: {
            retry: 0,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={client}>
      {children}
      {env.VITE_ENVIRONMENT === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};
